import type {
    input,
    output,
    core,
    ZodArray,
    ZodBoolean,
    ZodDefault,
    ZodEnum,
    ZodNullable,
    ZodNumber,
    ZodObject,
    ZodOptional,
    ZodType,
    ZodString
} from 'zod'
import { z } from 'zod'
import type { Prettify } from './utility'
import type { MaybePromise } from 'rollup'
import type { FormAutoSubmitOn, FormInitialData, FormSubmitOrigin } from '../composables/form'
import type { DeepReadonly } from 'vue'
import type { ApiErrorData } from '@composable-api/api.response-error'

// type FormInput = 'color' | 'date' | 'datetime-local' | 'file' | 'month'| 'range' | 'time' | 'week' | TextFormInput

export type TextFormInput = 'email' | 'number' | 'password' | 'search' | 'tel' | 'text' | 'url' | 'textarea'

export type FormAutocomplete = 'on' | 'off' | 'name' | 'given-name' | 'family-name' | 'email' | 'username' | 'new-password' | 'current-password'
    | 'one-time-code' | 'organization-title' | 'organization' | 'street-address' | 'address-line1' | 'address-line2'
    | 'address-line3' | 'country' | 'country-name' | 'postal-code' | 'cc-name' | 'cc-number' | 'cc-exp' | 'cc-exp-month'
    | 'cc-exp-year' | 'cc-csc' | 'cc-type' | 'transaction-currency' | 'transaction-amount' | 'language' | 'bday'
    | 'bday-day' | 'bday-month' | 'bday-year' | 'sex' | 'tel' | 'tel-country-code' | 'tel-national' | 'tel-area-code'
    | 'tel-local' | 'tel-local-prefix' | 'tel-local-suffix' | 'tel-extension' | 'impp' | 'url' | 'photo' | 'address-level1'
    | 'address-level2' | 'address-level3' | 'address-level4' | ShippingAndBillingFormAutocomplete

type ShippingAndBillingFormAutocomplete =  'shipping address-line1' | 'billing address-line1' | 'shipping address-level2' | 'billing address-level2'
    | 'shipping postal-code' | 'billing postal-code' | 'shipping country-name' | 'billing country-name'

export const ModelAction = {
    CREATE: 'create',
    EDIT: 'edit',
    DELETE: 'delete',
} as const
export type ModelAction = (typeof ModelAction)[keyof typeof ModelAction]

// TODO: this is a workaround for a Zod bug probably
// https://github.com/simplo-sro/simploshop-fe/issues/904
type AllKeys<T> = T extends T ? keyof T : never

export type FormObject<TSchema extends ZodObject> = {
    schema: ComputedRef<TSchema>
    globalError: Readonly<Ref<FormGlobalErrorValue>>
    isSubmitting: Readonly<Ref<boolean>>

    formDataObject: Ref<FormDataObject<TSchema>>
    formValues: Ref<input<TSchema>>
    initialData: ComputedRef<FormInitialData<TSchema> | undefined>
    formFieldErrors: DeepReadonly<Ref<FormErrors<TSchema>>>

    setFormValues: (values: FormInitialData<TSchema>) => void
    setFieldErrors: (errors: FormErrors<TSchema>) => void
    setGlobalError: (error: FormGlobalErrorValue) => void
    resetFieldError: (field: AllKeys<input<TSchema>>) => void
    resetForm: (ignoreInitialData?: boolean) => void
    resetFormData: (ignoreInitialData?: boolean) => void
    validateForm: () => Promise<boolean>
    submitRaw: () => Promise<void>

    _private: {
        _handleSubmit: (origin?: FormSubmitOrigin) => MaybePromise<void>
        _callBeforeSubmit: (callback: () => MaybePromise<boolean | undefined>) => void
        _autoSubmitOn: MaybeRefOrGetter<FormAutoSubmitOn | undefined>
        _fieldErrors: DeepReadonly<Ref<FormErrors<TSchema>>>
        _isDisabled: ComputedRef<boolean>
    }
}

export type GlobalFormError = {
    id: string | undefined  // is undefined when there's no error
    message: string | null
    type?: ApiErrorData['type'] | null
    hasFieldErrors?: boolean // indicates if there are field errors in the form
}

export type FormGlobalErrorValue = { message: GlobalFormError['message'], type?: GlobalFormError['type'], hasFieldErrors?: GlobalFormError['hasFieldErrors'] } | null
export type FormErrorValue = string | null
export type FormErrors<T extends ZodType> = Partial<Record<keyof input<T>, FormErrorValue>>

export type FormEventType = 'blur' | 'change' | '_set-val'

export type FormEvent<T extends FormFieldObject<any> = any> = {
    type: FormEventType
    __f: T['__f']
}

export type FormFieldObject<T, F = string> = {
    __f: F
    __r: boolean,
    __v: T | undefined
}

export type BaseZodType = ZodString | ZodNumber | ZodBoolean | ZodEnum<any>
export type SupportedZodTypes = ZodOptional<any> | ZodDefault<any> | ZodNullable<any> | BaseZodType | ZodObject<core.$ZodShape> | ZodArray<SupportedZodTypes>

export type WrapperZodType<T extends ZodType> = ZodOptional<T> | ZodDefault<T> | ZodNullable<T>

/**
 * Unwraps one level of Zod types, removing ZodOptional or ZodDefault wrappers.
 */
export type ZodUnwrap<T> =
    T extends WrapperZodType<infer U> ? U
        : T extends BaseZodType | ZodObject<core.$ZodShape> | core.$ZodStringFormatTypes ? T :
            never

/**
 * Deeply unwraps Zod types, removing all layers of ZodOptional and ZodDefault wrappers.
 */
export type ZodUnwrapDeep<T> =
    T extends WrapperZodType<infer U> ? ZodUnwrapDeep<U> :
        T extends BaseZodType | core.$ZodStringFormatTypes ? T :
            never

export type BaseFormFieldObject<T extends output<BaseZodType>>  = Omit<FormFieldObject<T>, '__f'>

type ZodObjectShape<T> = T extends ZodObject ? T['shape'] : never

export type FormDataObject<T extends ZodObject> = Required<{
    [key in keyof ZodObjectShape<T>]: ZodObjectShape<T>[key] extends ZodObject
        // recursively handle ZodObjects inside the schema
        ? Prettify<
            FormDataObject<ZodObjectShape<T>[key]>
        >
        // handle individual fields
        : Prettify<
            FormFieldObject<
                output<
                    ZodUnwrapDeep<
                        ZodObjectShape<T>[key]
                    >
                >,
                key
            >
        >
}>

export interface FormInputDataAttrs {}
